// Lib
import { isArray, isBoolean, isFinite, isNull, isPlainObject } from 'lodash';
import TaskItem from '@tiptap/extension-task-item';

import { Attribute } from '@tiptap/core';
import { TaskItemOptions } from '@tiptap/extension-task-item/src/task-item';

// Utils
import { parseTaskItemHtmlAttr, renderTaskItemHtmlAttr } from './taskListItemHtmlAttrUtils';

// Types
import type { TaskItemChipAttrs, TaskItemDueDate } from './taskListItemChipTypes';

/**
 * Shape check for a `dueDate` attr, which holds a single due date object or nothing.
 *
 * The timestamp is required because the chips renderer treats a present due date as displayable.
 */
const isDueDateValue = (value: unknown): boolean =>
    isNull(value) || (isPlainObject(value) && isFinite((value as Partial<TaskItemDueDate>).timestamp));

/**
 * Shape check for a chip attr holding a list of chip records.
 *
 * The members matter as much as the container: the chips renderer reads fields off each one
 * (e.g. a label's `labelId`), so a list of scalars or nulls would throw during its React render.
 */
const isChipListValue = (value: unknown): boolean => isArray(value) && value.every(isPlainObject);

/**
 * Builds the schema config for one chip attr, giving it a private `data-*` HTML representation
 * so the chip data survives the HTML clipboard (see `taskListItemHtmlAttrUtils`).
 *
 * `defaultValue` is passed once and reused for the schema default, the rendered attribute, and
 * the parsed fallback, so the three can never disagree. `isValid` guards the parsed value: the
 * HTML may come from another app or a damaged copy, and a wrongly shaped attr would reach the
 * chips renderer.
 *
 * `keepOnSplit: false` so pressing Enter yields a blank task rather than inheriting the previous
 * task's due date/labels/reactions.
 */
const buildChipAttr = ({
    attrName,
    defaultValue,
    isValid,
}: {
    attrName: keyof TaskItemChipAttrs;
    defaultValue: unknown;
    isValid: (value: unknown) => boolean;
}): Attribute => ({
    default: defaultValue,
    keepOnSplit: false,
    renderHTML: (attributes) => renderTaskItemHtmlAttr({ attrName, value: attributes[attrName], defaultValue }),
    parseHTML: (element) => parseTaskItemHtmlAttr({ element, attrName, defaultValue, isValid }),
});

/**
 * A task item's schema: its chip attrs and how each survives the HTML clipboard.
 *
 * - Split from `TaskListItem` so the node's shape can be composed without its React node view.
 * - That view pulls in Roma and SCSS, which the server cannot require.
 * - Everything about editing a task - keymaps, input rules, plugins - lives with the view.
 */
export const TaskListItemSchema = TaskItem.extend<TaskItemOptions>({
    // One line of text, then the sub-tasks. Tiptap's own `paragraph block*` lets a task gain a
    // second paragraph (Enter on a selection spanning tasks, a multi-line paste, a browser-made
    // line break). The keyboard handlers then read that paragraph as an empty task. See WEB-17589.
    content: 'paragraph taskList?',

    addAttributes() {
        // Per-task chip data. Each attr also has a private `data-*` HTML representation
        // (rendered and parsed by `buildChipAttr`) purely so a copy carries its chip data
        // through the clipboard; nothing outside this schema should read those attributes.
        // The stable `taskId` is supplied separately by the `UniqueID` extension
        // (configured where the editor composes its extensions), mirroring the card editor.
        //
        // Keyed by `TaskItemChipAttrs` so the schema cannot gain a chip attr the type does not
        // know about, and so everything typed against that type - notably the reset the cut
        // plugin applies - has to account for a new one.
        const chipAttrs: Record<keyof Omit<TaskItemChipAttrs, 'taskId'>, Attribute> = {
            dueDate: buildChipAttr({ attrName: 'dueDate', defaultValue: null, isValid: isDueDateValue }),
            assignments: buildChipAttr({ attrName: 'assignments', defaultValue: [], isValid: isChipListValue }),
            labels: buildChipAttr({ attrName: 'labels', defaultValue: [], isValid: isChipListValue }),
            reactions: buildChipAttr({ attrName: 'reactions', defaultValue: [], isValid: isChipListValue }),
            // Gates the empty "Assign to" placeholder chip (mirrors the element
            // `showAssignments` flag): set when the Assign tool is used so the
            // placeholder persists even before an assignee is chosen, and stays
            // shown if the user dismisses the popup without picking anyone.
            showAssignments: buildChipAttr({ attrName: 'showAssignments', defaultValue: false, isValid: isBoolean }),
            // Gates the empty "Set due date" placeholder chip (mirrors the element
            // `showDueDate` flag): set when the Due date tool is used so the
            // placeholder persists even before a date is chosen, and stays shown
            // if the user dismisses the popup without picking one.
            showDueDate: buildChipAttr({ attrName: 'showDueDate', defaultValue: false, isValid: isBoolean }),
        };

        return {
            // Preserve the base task item attrs (e.g. `checked`).
            ...this.parent?.(),
            ...chipAttrs,
        };
    },
});
